<template>
  <v-container>
    <v-card class="mx-auto rounded-xl">
      <v-row justify="center">
        <v-col cols="12">
          <v-card class="pa-4" elevation="4">
            <v-card-title>
              {{ isEditing ? 'Editar Usuário' : 'Novo Usuário' }}
            </v-card-title>
            <v-card-text>

              <v-tabs v-model="currentTab" dark class="mb-18">
                <v-tab value="details">Detalhes</v-tab>
              </v-tabs>

              <v-form ref="formRef" @submit.prevent="handleSubmit">
                <v-window v-model="currentTab" class="mt-2">
                  <v-window-item value="details">
                    <v-row class="mt-2">
                      <v-col cols="6">
                        <v-text-field v-model="formValue.name" label="Nome" :rules="[rules.name]"></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field v-model="formValue.phone" label="Telefone" v-maska="'(##) #####-####'"
                          :rules="[rules.phone]"></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field v-model="formValue.email" label="E-mail" :rules="[rules.email]"></v-text-field>
                      </v-col>

                      <v-col cols="6" v-if="!isEditing">
                        <v-text-field v-model="formValue.password"
                          :label="isEditing ? 'Nova Senha (opcional)' : 'Senha'"
                          :type="showPassword ? 'text' : 'password'" :rules="passwordRules"
                          :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                          @click:append="showPassword = !showPassword"></v-text-field>
                      </v-col>
                    </v-row>

                    <v-row class="mt-2">
                      <v-col cols="12" sm="6" md="3">
                        <v-switch v-model="formValue.is_active" color="success" label="Usuário Ativo?"></v-switch>
                      </v-col>

                      <v-col cols="12" sm="6" md="3">
                        <v-select v-model="formValue.role" :items="profiles" item-title="text" item-value="value"
                          label="Perfil" :rules="[rules.role]" clearable></v-select>
                      </v-col>

                      <v-col cols="12" md="12">
                        <v-autocomplete v-model="formValue.permissions" :items="availablePermissions"
                          item-title="description" item-value="id" label="Permissões" multiple chips />
                      </v-col>
                    </v-row>

                  </v-window-item>
                </v-window>
              </v-form>
            </v-card-text>

            <v-card-actions class="d-flex justify-space-between">
              <v-btn color="secondary" variant="flat" @click="handleCancel">
                <v-icon left>mdi-arrow-left</v-icon>
                Voltar
              </v-btn>
              <v-btn color="primary" variant="flat" :loading="isSubmitting" :disabled="isSubmitting"
                @click="handleSubmit">
                <v-icon left>mdi-content-save</v-icon>
                {{ isEditing ? 'Salvar Alterações' : 'Cadastrar Usuário' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { vMaska } from "maska/vue";
import api from '@/services/api';
import { useToastStore } from '@/store/toast';
import { useLoadingOverlay } from '@/store/loadingOverlay';

const toastStore = useToastStore();
const loadingOverlay = useLoadingOverlay();
const route = useRoute();
const router = useRouter();
const formRef = ref(null);

const currentTab = ref('details');
const isSubmitting = ref(false);
const showPassword = ref(false);

const formValue = ref({
  name: '',
  creci: null,
  phone: '',
  email: '',
  password: '',
  is_active: true,
  role: null,
});

const availablePermissions = ref([]);

const profiles = [
  { text: 'Corretor', value: 'broker' },
  { text: 'Corretor Lider', value: 'leadbroker' },
  { text: 'Corretor Administrador', value: 'adminbroker' },
  { text: 'Administrativo', value: 'administrative' },
];

const isEditing = computed(() => !!route.params.id);

const rules = {
  name: v => !!v || 'O nome é obrigatório.',
  email: v => !!v || 'O e-mail é obrigatório.',
  phone: v => !!v || 'O Telefone é obrigatório.',
  role: v => !!v || 'O Perfil é obrigatório.',
};

const passwordRules = computed(() => {
  if (isEditing.value) return [];
  return [
    v => !!v || 'A senha é obrigatória.',
    v => (v && v.length >= 6) || 'A senha deve ter no mínimo 6 caracteres.'
  ];
});

const loadUser = async (id) => {
  loadingOverlay.startLoading();
  try {
    const response = await api.get(`/users/${id}`);
    const data = response.data.data;

    if (data) {
      formValue.value = {
        name: data.name,
        phone: data.phone,
        email: data.email,
        password: '',
        is_active: data.is_active === 1,
        permissions: data.permissions.map(p => p.id),
        role: data.role,
      };


    } else {
      toastStore.showToast({ message: 'O usuário não foi encontrado.', color: 'warning' });
    }

  } catch (error) {
    console.error('Erro ao carregar os dados do usuário:', error);
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao carregar os dados do usuário',
      color: 'error',
    });
  } finally {
    loadingOverlay.stopLoading();
  }
};


const loadPermission = async (id) => {
  loadingOverlay.startLoading();
  try {
    const response = await api.get(`/permissions`);

    availablePermissions.value = response.data;

  } catch (error) {
    console.error('Erro ao carregar permissões:', error);
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao carregar permissões',
      color: 'error',
    });
  } finally {
    loadingOverlay.stopLoading();
  }
};

const handleSubmit = async () => {
  const { valid } = await formRef.value.validate();
  if (!valid) {
    currentTab.value = 'details';
    return;
  }

  isSubmitting.value = true;

  const payload = { ...formValue.value };
  if (isEditing.value && !payload.password) {
    delete payload.password;
  }

  payload.is_active = payload.is_active ? 1 : 0;

  try {
    if (isEditing.value) {
      await api.put(`/users/${route.params.id}`, payload);
    } else {
      await api.post('/users', payload);
    }

    router.push('/app/usuarios');
    toastStore.showToast({
      message: isEditing.value ? 'Usuário atualizado com sucesso!' : 'Usuário cadastrado com sucesso!',
      color: 'success',
    });

  } catch (error) {
    console.error('Erro ao salvar usuário:', error);
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao salvar usuário',
      color: 'error',
    });
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  router.push('/app/usuarios');
};

onMounted(async () => {
  await loadPermission();
  if (isEditing.value) {
    loadUser(route.params.id);
  }
});
</script>
